import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import { resolvePopBasicLinkHref } from './POP_BASIC_FileUtil';
import { useSave3, useSearch2 } from './POP_BASIC_QUERY';

function PopBasicExternalLink({ href, children, sx = {} }) {
  const url = resolvePopBasicLinkHref(href);
  if (!url) return null;
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: 'primary.main',
        fontSize: 13,
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const getHideKey = (popId, userId) => `pop_hide_${popId}_${userId}`;

const isHiddenToday = (popId, userId) => {
  if (!popId || !userId) return false;
  const raw = localStorage.getItem(getHideKey(popId, userId));
  if (!raw) return false;
  return dayjs().isBefore(dayjs(raw).endOf('day'));
};

const setHideToday = (popId, userId) => {
  if (!popId || !userId) return;
  localStorage.setItem(getHideKey(popId, userId), dayjs().format('YYYY-MM-DD'));
};

function PopToastTopRight({ popup, onClose, onHideToday }) {
  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        top: 24,
        right: 24,
        zIndex: 1400,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        p: 2,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {popup.title}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{ maxHeight: 160, overflow: 'auto', fontSize: 14, mb: 1 }}
        dangerouslySetInnerHTML={{ __html: popup.content || '' }}
      />
      {popup.linkUrl ? (
        <PopBasicExternalLink href={popup.linkUrl}>자세히 보기</PopBasicExternalLink>
      ) : null}
      {popup.isHideToday === 'Y' ? (
        <Button size="small" sx={{ mt: 1, display: 'block' }} onClick={onHideToday}>
          오늘 하루 보지 않기
        </Button>
      ) : null}
    </Paper>
  );
}

function PopToast({ popup, onClose, onHideToday }) {
  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1400,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        p: 2,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {popup.title}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{ maxHeight: 160, overflow: 'auto', fontSize: 14, mb: 1 }}
        dangerouslySetInnerHTML={{ __html: popup.content || '' }}
      />
      {popup.linkUrl ? (
        <PopBasicExternalLink href={popup.linkUrl}>자세히 보기</PopBasicExternalLink>
      ) : null}
      {popup.isHideToday === 'Y' ? (
        <Button size="small" sx={{ mt: 1, display: 'block' }} onClick={onHideToday}>
          오늘 하루 보지 않기
        </Button>
      ) : null}
    </Paper>
  );
}

function PopModal({ popup, onClose, onHideToday, onAgreeSave, agreeSaving }) {
  const needAgree = popup.isForceClose === 'Y' || popup.popType === 'FORCE_CONFIRM';
  const [agreed, setAgreed] = useState(false);

  const handleClose = async () => {
    if (needAgree && !agreed) return;
    if (needAgree && onAgreeSave) {
      const ok = await onAgreeSave(popup);
      if (!ok) return;
    }
    onClose();
  };

  return (
    <Dialog open maxWidth="md" fullWidth onClose={needAgree ? undefined : handleClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700 }}>
          {popup.title}
        </Typography>
        {!needAgree ? (
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{ fontSize: 15, lineHeight: 1.7 }}
          dangerouslySetInnerHTML={{ __html: popup.content || '' }}
        />
        {popup.linkUrl ? (
          <PopBasicExternalLink href={popup.linkUrl} sx={{ mt: 2, display: 'inline-block' }}>
            관련 링크 열기
          </PopBasicExternalLink>
        ) : null}
        {needAgree ? (
          <FormControlLabel
            sx={{ mt: 2, display: 'flex' }}
            control={
              <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            }
            label="내용을 확인하였으며 동의합니다."
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
        <Box>
          {popup.isHideToday === 'Y' && !needAgree ? (
            <Button size="small" onClick={onHideToday}>
              오늘 하루 보지 않기
            </Button>
          ) : null}
        </Box>
        <Button
          variant="contained"
          onClick={handleClose}
          disabled={(needAgree && !agreed) || agreeSaving}
        >
          {agreeSaving ? '저장 중…' : needAgree ? '동의 후 닫기' : '닫기'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * 대시보드(Home) 팝업 노출 레이어 — MODAL / TOAST_TR / TOAST_BR
 */
function PopBasicDashboardLayer() {
  const Dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const coCd = userInfo?.coCd;
  const userid = userInfo?.userid;
  const [dismissedIds, setDismissedIds] = useState([]);
  const [agreeSaving, setAgreeSaving] = useState(false);

  const { data: popupRows = [] } = useSearch2({ userInfo });
  const saveAgreeMutation = useSave3({ userInfo });

  const dismiss = useCallback((popId) => {
    setDismissedIds((prev) => (prev.includes(popId) ? prev : [...prev, popId]));
  }, []);

  const visiblePopups = useMemo(() => {
    const now = dayjs();
    return popupRows
      .filter((p) => p.useYn === 'Y')
      .filter((p) => {
        const start = p.dispStartDt ? dayjs(p.dispStartDt) : null;
        const end = p.dispEndDt ? dayjs(p.dispEndDt) : null;
        if (start?.isValid() && now.isBefore(start)) return false;
        if (end?.isValid() && now.isAfter(end)) return false;
        if (isHiddenToday(p.popId, userid)) return false;
        return true;
      })
      .filter((p) => !dismissedIds.includes(p.popId))
      .sort(
        (a, b) => Number(a.dispPriority ?? 1) - Number(b.dispPriority ?? 1)
      );
  }, [popupRows, userid, dismissedIds]);

  const savePopAgree = useCallback(
    async (row) => {
      const results = await saveAgreeMutation.mutateAsync([row]);
      const first = results?.[0];
      if (first && first.isSuccess === false) {
        throw new Error(first.message || '동의 저장에 실패했습니다.');
      }
      await queryClient.invalidateQueries({
        queryKey: ['PopupManagement', 'getPopBasicR2', coCd, userid],
      });
      return true;
    },
    [coCd, queryClient, saveAgreeMutation, userid]
  );

  const handleAgreeSave = useCallback(
    async (popup) => {
      setAgreeSaving(true);
      try {
        await savePopAgree({
          popId: popup.popId,
          agreeYn: 'Y',
        });
        return true;
      } catch (e) {
        Dispatch(
          showSnackbar({
            message: e?.message || '동의 내용 저장에 실패했습니다.',
            type: 'error',
          })
        );
        return false;
      } finally {
        setAgreeSaving(false);
      }
    },
    [Dispatch, savePopAgree]
  );

  const hideToday = useCallback(
    async (popId) => {
      try {
        await savePopAgree({
          popId,
          agreeYn: 'N',
          hideUntilDt: dayjs().format('YYYY-MM-DD'),
        });
      } catch (e) {
        Dispatch(
          showSnackbar({
            message: e?.message || '설정 저장에 실패했습니다.',
            type: 'warning',
          })
        );
      }
      setHideToday(popId, userid);
      dismiss(popId);
    },
    [Dispatch, dismiss, savePopAgree, userid]
  );

  const modalPopups = visiblePopups.filter((p) => (p.displayLayer || 'MODAL') === 'MODAL');
  const toastTrPopups = visiblePopups.filter(
    (p) => p.displayLayer === 'TOAST_TR' || p.displayLayer === 'BANNER_TOP'
  );
  const toastBrPopups = visiblePopups.filter((p) => p.displayLayer === 'TOAST_BR');

  const topModal = modalPopups[0];
  const topToastTr = toastTrPopups[0];
  const topToastBr = toastBrPopups[0];

  if (!topModal && !topToastTr && !topToastBr) return null;

  return (
    <>
      {topToastTr ? (
        <PopToastTopRight
          popup={topToastTr}
          onClose={() => dismiss(topToastTr.popId)}
          onHideToday={() => hideToday(topToastTr.popId)}
        />
      ) : null}
      {topToastBr ? (
        <PopToast
          popup={topToastBr}
          onClose={() => dismiss(topToastBr.popId)}
          onHideToday={() => hideToday(topToastBr.popId)}
        />
      ) : null}
      {topModal ? (
        <PopModal
          popup={topModal}
          agreeSaving={agreeSaving}
          onAgreeSave={handleAgreeSave}
          onClose={() => dismiss(topModal.popId)}
          onHideToday={() => hideToday(topModal.popId)}
        />
      ) : null}
    </>
  );
}

export default PopBasicDashboardLayer;
